import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  User, Clock, LogOut, Save, Bell, BellOff, Send, 
  Download, Trash2, BarChart3, Calendar, Edit2, Check, X,
  Shield
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { usePushNotifications } from "../hooks/usePushNotifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// State colors for visualization - Updated for readability
const STATE_COLORS = {
  Ground: "#4CAF82",
  Observe: "#4A90C4",
  Connect: "#B06BB0",
  Execute: "#D4723A",
  Integrate: "#7BAF4A"
};

export const Settings = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [wakeTime, setWakeTime] = useState(user?.wake_time || "06:00");
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [activity, setActivity] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  
  // Profile editing
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  
  // SLM Consent
  const [slmConsent, setSlmConsent] = useState(user?.slm_training_consent ?? null);
  const [savingConsent, setSavingConsent] = useState(false);
  
  const {
    isSupported,
    isSubscribed,
    permission,
    loading: pushLoading,
    subscribe,
    unsubscribe,
    sendTestNotification
  } = usePushNotifications();

  useEffect(() => {
    fetchActivity();
  }, []);

  const fetchActivity = async () => {
    try {
      const response = await axios.get(`${API}/users/me/activity`, {
        withCredentials: true
      });
      setActivity(response.data);
    } catch (error) {
      console.error("Error fetching activity:", error);
    } finally {
      setLoadingActivity(false);
    }
  };

  const handleSaveWakeTime = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/me`, 
        { wake_time: wakeTime },
        { withCredentials: true }
      );
      setUser(prev => ({ ...prev, wake_time: wakeTime }));
      toast.success("Wake time updated!");
    } catch (error) {
      console.error("Error updating wake time:", error);
      toast.error("Failed to update wake time");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveName = async () => {
    if (!newName.trim()) return;
    setSavingName(true);
    try {
      await axios.put(`${API}/users/me/profile`, 
        { name: newName.trim() },
        { withCredentials: true }
      );
      setUser(prev => ({ ...prev, name: newName.trim() }));
      setEditingName(false);
      toast.success("Name updated!");
    } catch (error) {
      console.error("Error updating name:", error);
      toast.error("Failed to update name");
    } finally {
      setSavingName(false);
    }
  };

  const handleSLMConsentToggle = async () => {
    setSavingConsent(true);
    const newConsent = !slmConsent;
    try {
      await axios.put(`${API}/users/me/slm-consent`, 
        { consent: newConsent },
        { withCredentials: true }
      );
      setSlmConsent(newConsent);
      setUser(prev => ({ ...prev, slm_training_consent: newConsent }));
      toast.success(newConsent ? "Consent granted" : "Consent withdrawn");
    } catch (error) {
      console.error("Error updating SLM consent:", error);
      toast.error("Failed to update consent");
    } finally {
      setSavingConsent(false);
    }
  };

  const handleToggleNotifications = async () => {
    if (isSubscribed) {
      const success = await unsubscribe();
      if (success) {
        toast.success("Notifications disabled");
      } else {
        toast.error("Failed to disable notifications");
      }
    } else {
      const success = await subscribe();
      if (success) {
        toast.success("Notifications enabled! You'll receive morning reminders.");
      } else if (permission === 'denied') {
        toast.error("Notifications blocked. Please enable them in your browser settings.");
      } else {
        toast.error("Failed to enable notifications");
      }
    }
  };

  const handleSendTestNotification = async () => {
    setSendingTest(true);
    try {
      const result = await sendTestNotification();
      if (result.status === 'sent') {
        toast.success("Test notification sent! Check your notifications.");
      } else {
        toast.error(result.reason || "Failed to send test notification");
      }
    } catch (error) {
      toast.error("Failed to send test notification");
    } finally {
      setSendingTest(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/users/me/export`, {
        withCredentials: true
      });
      
      // Download as JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `clarityos_data_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Data exported successfully!");
    } catch (error) {
      console.error("Error exporting data:", error);
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/me/account`, {
        withCredentials: true
      });
      
      toast.success("Account deleted successfully");
      setUser(null);
      navigate("/");
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error("Failed to delete account");
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      navigate("/");
    } catch (error) {
      console.error("Error logging out:", error);
      setUser(null);
      navigate("/");
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight mb-2">
          Settings
        </h1>
        <p className="text-slate-400">Manage your account and preferences</p>
      </div>

      {/* Profile Card */}
      <Card className="bg-clarity-paper/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
            <User className="w-5 h-5 text-brand-amber" />
            Profile
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-clarity-subtle text-slate-400 text-xl">
                {user?.name?.charAt(0) || "?"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="bg-clarity-subtle/50 border-white/10 text-white max-w-[200px]"
                    placeholder="Your name"
                    autoFocus
                  />
                  <Button
                    onClick={handleSaveName}
                    disabled={savingName || !newName.trim()}
                    size="icon"
                    className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => {
                      setEditingName(false);
                      setNewName(user?.name || "");
                    }}
                    size="icon"
                    variant="ghost"
                    className="text-slate-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-xl font-medium text-white" data-testid="user-name">
                    {user?.name || "Anonymous"}
                  </p>
                  <button
                    onClick={() => setEditingName(true)}
                    className="text-slate-500 hover:text-white transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </div>
              )}
              <p className="text-slate-400" data-testid="user-email">
                {user?.email || "No email"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity Stats Card */}
      <Card className="bg-clarity-paper/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-brand-tertiary" />
            Your Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingActivity ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-2 border-brand-amber border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : activity ? (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-clarity-subtle/30 rounded-lg p-4">
                  <p className="text-2xl font-bold text-white">{activity.total_sessions}</p>
                  <p className="text-xs text-slate-400">Protocol Sessions</p>
                </div>
                <div className="bg-clarity-subtle/30 rounded-lg p-4">
                  <p className="text-2xl font-bold text-white">{activity.journal_entries}</p>
                  <p className="text-xs text-slate-400">Journal Entries</p>
                </div>
                <div className="bg-clarity-subtle/30 rounded-lg p-4">
                  <p className="text-2xl font-bold text-white">{activity.scenario_completions}</p>
                  <p className="text-xs text-slate-400">Scenarios Done</p>
                </div>
                <div className="bg-clarity-subtle/30 rounded-lg p-4">
                  <p className="text-2xl font-bold text-white">{activity.total_time_minutes}</p>
                  <p className="text-xs text-slate-400">Minutes Practiced</p>
                </div>
              </div>

              {/* State Distribution */}
              {activity.state_distribution && Object.values(activity.state_distribution).some(v => v > 0) && (
                <div>
                  <p className="text-sm text-slate-400 mb-3">State Distribution in Journal</p>
                  <div className="flex items-center gap-1 mb-2">
                    {Object.entries(activity.state_distribution).map(([state, count]) => {
                      const total = Object.values(activity.state_distribution).reduce((a, b) => a + b, 0);
                      const percentage = total > 0 ? (count / total) * 100 : 0;
                      return (
                        <div 
                          key={state}
                          className="h-2 rounded-full"
                          style={{ 
                            width: `${Math.max(percentage, count > 0 ? 5 : 2)}%`, 
                            backgroundColor: STATE_COLORS[state] || '#666'
                          }}
                          title={`${state}: ${count}`}
                        />
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs">
                    {Object.entries(activity.state_distribution).map(([state, count]) => (
                      <div key={state} className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STATE_COLORS[state] }} />
                        <span className="text-slate-400">{state}: {count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Member Since */}
              {activity.member_since && (
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Calendar className="w-4 h-4" />
                  <span>Member since {new Date(activity.member_since).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-slate-400">Unable to load activity data</p>
          )}
        </CardContent>
      </Card>

      {/* Wake Time Card */}
      <Card className="bg-clarity-paper/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-brand-tertiary" />
            Wake Time
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-slate-400 text-sm">
            Set your ideal wake time to track how long since you've been awake.
            The countdown timer on your dashboard will show time elapsed since this moment.
          </p>
          
          <div className="flex items-end gap-4">
            <div className="flex-1 max-w-[200px]">
              <Label className="text-slate-400 mb-2 block">Wake Time</Label>
              <Input
                type="time"
                value={wakeTime}
                onChange={(e) => setWakeTime(e.target.value)}
                className="bg-clarity-subtle/50 border-white/10 text-white focus:border-brand-amber"
                data-testid="wake-time-input"
              />
            </div>
            <Button
              onClick={handleSaveWakeTime}
              disabled={saving || wakeTime === user?.wake_time}
              data-testid="save-wake-time-btn"
              className="bg-brand-amber text-clarity-bg font-semibold px-6 hover:bg-amber-400 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Push Notifications Card */}
      <Card className="bg-clarity-paper/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
            <Bell className="w-5 h-5 text-brand-secondary" />
            Push Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isSupported ? (
            <div className="text-slate-400 text-sm">
              Push notifications are not supported in this browser.
            </div>
          ) : (
            <>
              <p className="text-slate-400 text-sm">
                Get daily morning reminders to complete your ClarityOS protocol.
                Notifications will be sent based on your wake time.
              </p>
              
              <div className="flex items-center justify-between p-4 bg-clarity-subtle/30 rounded-lg">
                <div className="flex items-center gap-3">
                  {isSubscribed ? (
                    <Bell className="w-5 h-5 text-green-400" />
                  ) : (
                    <BellOff className="w-5 h-5 text-slate-500" />
                  )}
                  <div>
                    <p className="font-medium text-white">
                      {isSubscribed ? "Notifications Enabled" : "Notifications Disabled"}
                    </p>
                    <p className="text-sm text-slate-400">
                      {isSubscribed 
                        ? "You'll receive morning protocol reminders" 
                        : "Enable to get daily reminders"}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleToggleNotifications}
                  disabled={pushLoading}
                  variant={isSubscribed ? "outline" : "default"}
                  data-testid="toggle-notifications-btn"
                  className={isSubscribed 
                    ? "border-white/20 text-slate-300 hover:bg-white/5" 
                    : "bg-brand-amber text-clarity-bg font-semibold hover:bg-amber-400"}
                >
                  {pushLoading ? "..." : isSubscribed ? "Disable" : "Enable"}
                </Button>
              </div>

              {isSubscribed && (
                <div className="flex items-center justify-between p-4 bg-clarity-subtle/30 rounded-lg">
                  <div>
                    <p className="font-medium text-white">Test Notification</p>
                    <p className="text-sm text-slate-400">
                      Send a test notification to verify it's working
                    </p>
                  </div>
                  <Button
                    onClick={handleSendTestNotification}
                    disabled={sendingTest}
                    variant="outline"
                    data-testid="send-test-notification-btn"
                    className="border-white/20 text-slate-300 hover:bg-white/5"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sendingTest ? "Sending..." : "Send Test"}
                  </Button>
                </div>
              )}

              {permission === 'denied' && (
                <div className="text-amber-400 text-sm bg-amber-500/10 p-3 rounded-lg">
                  Notifications are blocked in your browser. To enable them, click the lock icon 
                  in your browser's address bar and allow notifications for this site.
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Privacy & Data Consent Card */}
      <Card className="bg-clarity-paper/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#B06BB0]" />
            Privacy
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-clarity-subtle/30 rounded-lg">
            <div className="flex-1 pr-4">
              <p className="font-medium text-white">Strata Leadership Model Training</p>
              <p className="text-sm text-slate-400 mt-1">
                Allow anonymised and aggregated session data to be used to train the Strata Leadership Model - 
                an AI model built specifically to understand leadership state transitions. 
                Your data is never shared individually.
              </p>
            </div>
            <Button
              onClick={handleSLMConsentToggle}
              disabled={savingConsent}
              variant={slmConsent ? "default" : "outline"}
              data-testid="slm-consent-toggle"
              className={slmConsent 
                ? "bg-[#4CAF82] text-white hover:bg-[#3d9a6a] min-w-[100px]" 
                : "border-white/20 text-slate-300 hover:bg-white/5 min-w-[100px]"}
            >
              {savingConsent ? "..." : slmConsent ? "Consented" : "Grant Consent"}
            </Button>
          </div>
          
          {slmConsent === false && (
            <p className="text-amber-400 text-sm">
              You have not consented to SLM training. You can grant consent at any time.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Data Export Card */}
      <Card className="bg-clarity-paper/50 border-white/10">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
            <Download className="w-5 h-5 text-sky-400" />
            Export Your Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-slate-400 text-sm">
            Download all your ClarityOS data including profile, protocol completions, 
            journal entries, and scenario completions as a JSON file.
          </p>
          <Button
            onClick={handleExportData}
            disabled={exporting}
            variant="outline"
            data-testid="export-data-btn"
            className="border-white/20 text-slate-300 hover:bg-white/5"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? "Exporting..." : "Download My Data"}
          </Button>
        </CardContent>
      </Card>

      {/* Logout Card */}
      <Card className="bg-clarity-paper/50 border-white/10">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-white">Sign Out</p>
              <p className="text-sm text-slate-400">
                Sign out of your ClarityOS account
              </p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              data-testid="logout-btn"
              className="border-white/20 text-slate-300 hover:bg-white/5"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delete Account Card */}
      <Card className="bg-clarity-paper/50 border-red-500/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-red-400">Delete Account</p>
              <p className="text-sm text-slate-400">
                Permanently delete your account and all associated data
              </p>
            </div>
            <Button
              onClick={() => setShowDeleteDialog(true)}
              variant="outline"
              data-testid="delete-account-btn"
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50 transition-colors"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Account
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-clarity-paper border-red-500/20">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete Account
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This action cannot be undone. All your data will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-300">
              This will permanently delete:
            </p>
            <ul className="text-sm text-slate-400 list-disc list-inside space-y-1">
              <li>Your profile and account</li>
              <li>All protocol completions</li>
              <li>All journal entries</li>
              <li>All scenario completions</li>
              <li>Notification subscriptions</li>
            </ul>
            
            <div className="space-y-2">
              <Label className="text-slate-400">Type DELETE to confirm:</Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                className="bg-clarity-subtle/50 border-red-500/30 text-white focus:border-red-500"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setShowDeleteDialog(false);
                setDeleteConfirmText("");
              }}
              className="text-slate-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText !== "DELETE" || deleting}
              className="bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
            >
              {deleting ? "Deleting..." : "Delete My Account"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
